import React, { useEffect, useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2'; // For beautiful SweetAlerts

const DisplayCategories = () => {
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [rows, setRows] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [userLabId, setUserLabId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Get userLabId from localStorage on mount
  useEffect(() => {
    const labId = localStorage.getItem('userLabId');
    if (labId) {
      setUserLabId(labId.trim());
    } else {
      setUserLabId(''); // Will fetch all categories if no lab ID
    }
  }, []);

  // Fetch categories from backend when userLabId is ready
  useEffect(() => {
    setLoading(true);
    setError(null);

    const fetchCategories = async () => {
      try {
        let apiUrl = 'https://hms.backend.hiragroupcloud.com/api/categories';
        
        // Add userLabId parameter only if it exists
        if (userLabId && userLabId.trim() !== '') {
          apiUrl += `?userLabId=${encodeURIComponent(userLabId.trim())}`;
        }

        const res = await fetch(apiUrl);
        
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.message || 'Failed to fetch categories');
        }

        const data = await res.json();

        if (Array.isArray(data)) {
          const formatted = data.map((item, index) => ({
            id: item._id || item.id || `temp-${index}`,
            name: item.name || 'No Name',
            sortrank: item.sortrank || '',
            customId: item.customId || '',
            addedBy: item.addedBy || 'N/A'
          }));
          setRows(formatted);
        } else {
          setRows([]);
        }
      } catch (err) {
        setError('Failed to load categories. Please check server or try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, [userLabId]);

  // Handle delete with confirmation and success alert
  const handleDelete = async (id) => {
    // First: Confirmation dialog
    const confirmResult = await Swal.fire({
      title: 'Are you sure?',
      text: 'You want to delete this category? This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, Delete',
      cancelButtonText: 'Cancel'
    });

    // If user clicks Cancel, do nothing
    if (!confirmResult.isConfirmed) return;

    try {
      const res = await fetch(`https://hms.backend.hiragroupcloud.com/api/categories/${id}`, { 
        method: 'DELETE' 
      });

      if (res.ok) {
        // Remove from UI
        setRows(prev => prev.filter(row => row.id !== id));
        setSelectedRowId(null);

        // Success message
        Swal.fire({
          icon: 'success',
          title: 'Deleted!',
          text: 'Category deleted successfully!',
          confirmButtonColor: '#004aad',
          timer: 2000,
          showConfirmButton: false
        });
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Failed',
          text: 'Failed to delete category.',
          confirmButtonColor: '#004aad'
        });
      }
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Error deleting category. Please try again.',
        confirmButtonColor: '#004aad'
      });
    }
  };

  // Handle edit button - shows info alert like your screenshot
  const handleEditClick = () => {
    Swal.fire({
      icon: 'info',
      title: 'Info',
      text: 'Edit functionality coming soon!',
      confirmButtonText: 'OK',
      confirmButtonColor: '#004aad',
      customClass: {
        popup: 'animated fadeIn',
        confirmButton: 'btn btn-primary'
      },
      buttonsStyling: false
    });
  };

  // Filter rows based on search term
  const filteredRows = rows.filter(row =>
    Object.values(row).some(value =>
      value && value.toString().toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  const columns = [
    { field: 'name', headerName: 'Name', width: 200 },
    { field: 'sortrank', headerName: 'Sort Rank', width: 150, align: 'center', headerAlign: 'center' },
    { field: 'customId', headerName: 'Custom ID', width: 150 },
    { field: 'addedBy', headerName: 'Added By (Lab ID)', width: 180 },
  ];

  return (
    <>
      <h2 className="title" >
        Display Categories
      </h2>
      <div style={{width:'1560px',margin:'0 auto',backgroundColor:"#f4f4f4",padding:"10px"}}>
      {/* Search + Add Button */}
      <div className="search mb-4">
        <div className="row align-items-center">
         
            <div className="col-md-6 d-flex justify-content-start">
              <input
                type="text"
                className="form-control"
                placeholder="Search categories..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                disabled={loading}
              />
            </div>
          
          <div className="col-md-6 d-flex justify-content-end gap-2">
            <button
              className="btn btn-primary"
              style={{ backgroundColor: "#004aad", borderColor: "#004aad" }}
              onClick={() => navigate('/add-category')}
              disabled={loading}
            >
              Add New Category
            </button>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading categories...</p>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {/* No Data State */}
      {!loading && !error && filteredRows.length === 0 && (
        <div className="text-center py-5 border rounded" style={{ backgroundColor: '#f8f9fa' }}>
          <h5>No categories found</h5>
          <p>
            {userLabId 
              ? `No categories added yet for Lab ID: ${userLabId}`
              : 'No categories found in the system'}
          </p>
          <button
            className="btn btn-primary"
            style={{ backgroundColor: "#004aad", borderColor: "#004aad" }}
            onClick={() => navigate('/add-category')}
          >
            Add Your First Category
          </button>
        </div>
      )}

      {/* Data Table */}
      {!loading && !error && filteredRows.length > 0 && (
        <>
          <div style={{ height: 400, width: '100%', position: 'relative', marginTop: '2%', }}>
            <DataGrid
              rows={filteredRows}
              columns={columns}
              pageSize={5}
              rowsPerPageOptions={[5, 10, 20]}
              onRowClick={(params) => setSelectedRowId(params.id)}
              sx={{
                '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 'bold' },
                '& .MuiDataGrid-cell:focus': { outline: 'none' },
                '& .MuiDataGrid-cell:focus-within': { outline: 'none' },
              }}
            />

            {/* Edit & Delete Buttons (appear when row selected) */}
            {selectedRowId !== null && (
              <div
                style={{
                  position: 'absolute',
                  top: rows.findIndex(row => row.id === selectedRowId) * 52 + 48,
                  right: '10px',
                  display: 'flex',
                  marginTop: '15px',
                  zIndex: 1000
                }}
              >
                <button
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#0d6efd',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    marginRight: '5px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                  }}
                  onClick={handleEditClick}
                >
                  Edit
                </button>
                <button
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#dc3545',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                  }}
                  onClick={() => handleDelete(selectedRowId)}
                >
                  Delete
                </button>
              </div>
            )}
          </div>
        </>
      )}
      </div>
    </>
  );
};

export default DisplayCategories;